import React, { useCallback, useMemo, useState } from 'react';
import Wrapper from 'components/layouts/Wrapper/Wrapper';
import { Headers, LoadingSpinner } from 'components/core';
import { Button } from 'components/core/Button/Button';
import ListEmpty from '../../Common/ListEmpty/ListEmpty';
import { PendingPayableRow, useGetPendingPayablesQuery } from 'services/reportsApi';
import { useInviteVendorsMutation } from 'services/vendorsApi';
import { isPermitted, useGetUserQuery } from 'services/userApi';
import {
  InviteVendorItemResult,
  InviteVendorsResponse,
  NotOnboardedVendor,
} from 'shared/models/services/vendors.model';
import OnboardManagedModal from 'components/modules/Vendors/NotOnboarded/OnboardManagedModal';
import PropertyFilter from './PropertyFilter';
import PendingPayablesSummary from './PendingPayablesSummary';
import PendingPayablesTable from './PendingPayablesTable';
import { summarizePendingPayables, uniqueProperties } from './summarize';
import { csvFilename, toCsv, triggerCsvDownload } from './csvExport';

// A pending-payable row carries the vendor's Coupa supplier id, name and EIN,
// which is enough to drive the shared OnboardManagedModal. Email and mailing
// address aren't on the report, so they open blank for the admin to fill in.
const rowToVendor = (row: PendingPayableRow): NotOnboardedVendor => ({
  identityKey: row.identityKey ?? '',
  coupaSupplierId: row.coupaSupplierId as string, // guarded by canOnboard/coupaSupplierId check
  name: row.vendorName ?? null,
  code: row.vendorCode ?? '',
  email: null,
  primaryContactName: null,
  phone: null,
  street1: null,
  street2: null,
  city: null,
  state: null,
  zip: null,
  taxId: row.ein ?? null,
  status: null,
  coupaRecordCount: 0,
  canInvite: row.canInvite,
});

export const PendingPayablesContainer: React.FC = () => {
  const { data, isLoading, isSuccess, refetch } = useGetPendingPayablesQuery();
  const [propertyId, setPropertyId] = useState<string | null>(null);

  const { data: user } = useGetUserQuery({});
  const canOnboard = !!isPermitted(user, 'onboard-vendor');
  const [onboardTarget, setOnboardTarget] = useState<NotOnboardedVendor | null>(null);

  const [inviteVendors] = useInviteVendorsMutation();
  const [invitingKey, setInvitingKey] = useState<string | null>(null);
  const [invitedKeys, setInvitedKeys] = useState<Set<string>>(new Set());
  const [inviteMessage, setInviteMessage] = useState<string>('');
  const [inviteError, setInviteError] = useState<boolean>(false);

  const rows = data?.rows ?? [];
  const properties = useMemo(() => uniqueProperties(rows), [rows]);
  const filteredRows = useMemo(
    () => (propertyId ? rows.filter((r) => r.propertyId === propertyId) : rows),
    [rows, propertyId]
  );
  const summary = useMemo(() => summarizePendingPayables(filteredRows), [filteredRows]);

  const handleExport = () => {
    if (filteredRows.length === 0) return;
    triggerCsvDownload(toCsv(filteredRows), csvFilename());
  };

  const handleInvite = useCallback(
    async (row: PendingPayableRow) => {
      if (!row.identityKey) return;
      setInvitingKey(row.identityKey);
      setInviteMessage('');
      const resp: InviteVendorsResponse | null = await inviteVendors({
        identityKeys: [row.identityKey],
      })
        .unwrap()
        .catch(() => null);
      setInvitingKey(null);
      if (!resp) {
        setInviteError(true);
        setInviteMessage('Failed to send invite. Please try again.');
        setTimeout(() => setInviteMessage(''), 6500);
        return;
      }
      const result = resp.results[0] as InviteVendorItemResult | undefined;
      if (result?.status === 'invited') {
        setInvitedKeys((prev) => new Set(prev).add(row.identityKey as string));
        setInviteError(false);
        setInviteMessage(`Invite sent to ${row.vendorName}.`);
      } else {
        setInviteError(true);
        setInviteMessage(
          result?.reason === 'no_contact'
            ? `${row.vendorName} has no contact email on file.`
            : `Could not invite ${row.vendorName}.`
        );
      }
      setTimeout(() => setInviteMessage(''), 6500);
    },
    [inviteVendors]
  );

  const handleOnboard = useCallback((row: PendingPayableRow) => {
    if (!row.coupaSupplierId) return;
    setOnboardTarget(rowToVendor(row));
  }, []);

  const handleOnboardSuccess = useCallback(() => {
    const name = onboardTarget?.name ?? 'Vendor';
    setOnboardTarget(null);
    setInviteError(false);
    setInviteMessage(`${name} onboarded. It can now be paid.`);
    setTimeout(() => setInviteMessage(''), 6500);
    refetch();
  }, [onboardTarget, refetch]);

  return (
    <Wrapper additionalClasses="flex-col items-start" pageTitle="Pending Payables Report">
      {isLoading && <LoadingSpinner />}
      <Headers headingContent="Pending Payables Report" />
      <p className="text-14s text-gray-600">
        Payables for vendors not yet registered on PayUp. Rows marked{' '}
        <span className="font-medium text-green-700">Funds held</span> have been approved and the
        funds collected — the vendor just needs to register to receive payment. Invite the vendor to
        speed this up.
      </p>
      {inviteMessage && (
        <p className={`text-14s ${inviteError ? 'text-red-600' : 'text-green-600'}`} role="status">
          {inviteMessage}
        </p>
      )}
      {isSuccess && rows.length === 0 && (
        <ListEmpty subText="No pending payables — every vendor with an outstanding payable is onboarded." />
      )}
      {isSuccess && rows.length > 0 && (
        <>
          <div className="flex w-full items-center justify-between">
            <PropertyFilter properties={properties} value={propertyId} onChange={setPropertyId} />
            <Button
              variants="extraSmall"
              buttonLabel="Export CSV"
              isDisabled={filteredRows.length === 0}
              onClick={handleExport}
              additionalClasses="h-9 px-4"
            />
          </div>
          <PendingPayablesSummary summary={summary} />
          {filteredRows.length === 0 ? (
            <ListEmpty subText="No pending payables for this property." />
          ) : (
            <PendingPayablesTable
              rows={filteredRows}
              onInvite={handleInvite}
              invitingKey={invitingKey}
              invitedKeys={invitedKeys}
              onOnboard={handleOnboard}
              canOnboard={canOnboard}
            />
          )}
        </>
      )}

      <OnboardManagedModal
        isOpen={!!onboardTarget}
        vendor={onboardTarget}
        onClose={() => setOnboardTarget(null)}
        onSuccess={handleOnboardSuccess}
      />
    </Wrapper>
  );
};

export default PendingPayablesContainer;
